import { isBefore, isValid, parseISO } from "date-fns";
import { getIn } from "final-form";
import { isPossiblePhoneNumber } from "libphonenumber-js";
import {
  choices,
  email,
  maxLength,
  minLength,
  number,
  regex,
  required,
} from "react-admin";
import { getMessage, memoize } from "./validation/validate";

export const validateName = [
  required(),
  regex(/\w\s\w/, "Must be a valid first and last name."),
  minLength(3),
  maxLength(50),
];

export const validateParticipantCode = [
  regex(/(^[A-Za-z]{4}$)/, "Must be a 4 letter code, or blank."),
];

export const validateNameLength = [required(), maxLength(256)];

export const validateNZPhoneNumber = (phoneNumber: string) => {
  return phoneNumber && isPossiblePhoneNumber(phoneNumber, "NZ")
    ? undefined
    : "Must be a valid NZ phone number";
};

export const validateInternationalPhoneNumber = (phoneNumber: string) => {
  return phoneNumber && isPossiblePhoneNumber(phoneNumber)
    ? undefined
    : "Must be a valid phone number";
};

export const validateLessThanOrEqualTo = memoize(
  (max: string, message = "portal.validation.lessThanOrEqualTo") => (
    value: string,
    values?: Record<string, unknown>
  ) =>
    value && Number(value) > Number(max)
      ? getMessage(message, { max }, value, values)
      : undefined
);

export const validateGreaterThanOrEqualTo = memoize(
  (min: string, message = "portal.validation.greaterThanOrEqualTo") => (
    value: string,
    values?: Record<string, unknown>
  ) =>
    value && Number(value) < Number(min)
      ? getMessage(message, { min }, value, values)
      : undefined
);

export const validateGreaterThan = memoize(
  (max, message = "portal.validation.greaterThan") => (value, values) =>
    value && !(Number(value) > Number(max))
      ? getMessage(message, { max }, value, values)
      : undefined
);

export const validateLessThan = memoize(
  (max, message = "portal.validation.lessThan") => (value, values) =>
    value && !(Number(value) < Number(max))
      ? getMessage(message, { max }, value, values)
      : undefined
);

export const validateBetween = memoize(
  (min, max, message = "portal.validation.between") => (value, values) =>
    value && (Number(value) < Number(min) || Number(value) > Number(max))
      ? getMessage(message, { min, max }, value, values)
      : undefined
);

const assetTypeNameLookup = {
  transmissionLines: "transmission lines",
  generators: "generators",
  transformers: "transformers",
  energyStorageDevices: "energy storage devices",
  reactivePowerDevices: "reactive power devices",
};

export const validateUniqueAssetName = memoize(
  (formData, source) => (value) => {
    const sourcePattern = source.match(/acs\.station\.(.*?)\[(.*?)\]/);
    const assetTypeToValidate = sourcePattern[1];
    const assetIndexToValidate = sourcePattern[2];

    let message;

    [
      "generators",
      "transmissionLines",
      "transformers",
      "energyStorageDevices",
      "reactivePowerDevices",
    ].forEach((assetType) => {
      const assetsData = getIn(formData, "acs.station." + assetType);
      if (assetsData) {
        Object.keys(assetsData).forEach((assetIndex) => {
          const assetSource =
            "acs.station." +
            assetType +
            "[" +
            assetIndex +
            "]" +
            "basicInformation.name.value";

          const assetName = getIn(formData, assetSource);

          if (
            value?.trim().toLowerCase() === assetName?.trim().toLowerCase() &&
            (assetIndexToValidate !== assetIndex ||
              assetTypeToValidate !== assetType)
          ) {
            message =
              "This asset name already exists in " +
              assetTypeNameLookup[assetType];
          }
        });
      }
    });

    return message;
  }
);

export function validateUniqueName(
  names: string[],
  message = "pocp.message.uniqueNameRequired"
) {
  return (name, values) =>
    choices(names)(name, values) === undefined ? message : undefined;
}

export const validateDateTime = [
  required(),
  regex(
    /([0-9][0-9]\/[0-9][0-9]\/20[0-9][0-9])\s[0-9][0-9]:[0-9][0-9]\s[a:p]m/,
    "Must be a valid date time.  Eg: 25/05/2021 01:30 pm"
  ),
];

export const validateFalseIndications = memoize(
  (
    message = "Potential compliance issue subject to Appendix A in Technical Code C of Part 8 of the Electricity Industry Participation Code"
  ) => (value, values) => (value && value === "No" ? message : undefined)
);

export const validateFalseProtections = memoize(
  (
    message = "Potential compliance issue subject to Technical Code A of Part 8 of the Electricity Industry Participation Code"
  ) => (value, values) => (value && value === "No" ? message : undefined)
);

export const validateDate = memoize(
  (message = "Must be a valid date") => (value) => {
    const date = parseISO(value);
    return value && isValid(date) ? undefined : message;
  }
);

export const validateEndDateLaterThanStartDate = memoize((value, values) => {
  if (!value || !values.timeStart) {
    return "portal.validation.startDateBlank";
  }
  if (isBefore(new Date(values.timeEnd), new Date(values.timeStart))) {
    return "portal.validation.endDateMustLaterThanStartDate";
  }
  return undefined;
});

export const validateDatePickerDate = memoize(
  (message = "Must be a valid date") => (value) => {
    if (isValid(value)) {
      return value && isValid(value) ? undefined : message;
    } else {
      const date = parseISO(value);
      return value && isValid(date) ? undefined : message;
    }
  }
);

export const validateDateEarlierThanToday = memoize(
  (message = "Must be a valid date no later than today") => (value) => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);

    const date = parseISO(value);

    return value && date < tomorrow ? undefined : message;
  }
);

export const validateAcceptableDate = [
  validateDate(),
  validateDateEarlierThanToday(),
];

export const validateTimeEnd = [
  required(),
  validateEndDateLaterThanStartDate,
  validateDatePickerDate(),
];
export const validateTimeStart = [required(), validateDatePickerDate()];

export const validateIfGenerationOrEmbedded = memoize(
  (category, message = "Must be present") => (value) => {
    return category === "generation" ? required() : undefined;
  }
);

export const validateFreqCompliance = memoize(
  (
    island,
    messageNorth = "Potential compliance issue subject to clause 8.19 in Part 8 of the Electricity Industry Participation Code, North Island generating units must be ≤ 47.5 Hz",
    messageSouth = "Potential compliance issue subject to clause 8.19 in Part 8 of the Electricity Industry Participation Code, South Island generating units must be ≤ 47 Hz"
  ) => (value) =>
    island && value && island === "North" && value > 47.5
      ? messageNorth
      : island && value && island === "South" && value > 47
      ? messageSouth
      : value && !island
      ? "Relevant Island must be entered in at the Station > General Information section"
      : undefined
);

export const validateAVRCompliance = memoize(
  (
    message = "Potential compliance issue subject to Technical Code A of Part B of the Electricity industry Participation Code"
  ) => (networkConnection) => (value) =>
    networkConnection &&
    value &&
    networkConnection.code === "TPNZ" &&
    value !== "Automatic Voltage Regulation (AVR)"
      ? message
      : undefined
);

export const validateEmail = [required(), email()];

export const validateFile = [required()];

const isResultValid = (result?: string) => result === undefined;

const validatePhoneNumber = (phoneNumber: string) => {
  const validateNZNumberResult = validateNZPhoneNumber(phoneNumber);

  return isResultValid(validateNZNumberResult)
    ? validateNZNumberResult
    : validateInternationalPhoneNumber(phoneNumber);
};

export const validatePhone = [required(), validatePhoneNumber];
export const validatePhoneOptional = (phoneNumber?: string) => {
  return phoneNumber === undefined
    ? undefined
    : validatePhoneNumber(phoneNumber);
};

export const validatePositiveInteger = regex(
  /^\d+$/,
  "Must be a positive whole number"
);

export const validatePositive = regex(
  /^\+?(\d+(\.\d(\d+)?)?)$/,
  "Must be a positive number"
);

export const validateNegative = regex(
  /^[-](\d+(\.\d(\d+)?)?)$/,
  "Must be a negative number"
);

export const validateNumber = regex(
  /^-?(\d+(\.\d(\d+)?)?)$/,
  "Must be a number"
);

export const validateRequiredPositive = [required(), validatePositive];

export const validateRequiredNegative = [required(), validateNegative];

export const validateRequiredNumber = [required(), validateNumber];

export const validateMandatoryPositive = [
  required("Mandatory"),
  validatePositive,
];
export const validateMandatoryNegative = [
  required("Mandatory"),
  validateNegative,
];
export const validateMandatoryNumber = [required("Mandatory"), validateNumber];

export const validateEvenNumber = memoize(
  (message = "Must be an even number") => (value: string) =>
    Number(value) % 2 === 0 ? undefined : message
);

export const validateRequiredEvenNumberGreaterThanOrEqualTo2 = [
  required(),
  number(),
  validateEvenNumber(),
  validateGreaterThanOrEqualTo("2"),
];
